"use client"

import React, { useState, useMemo, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import { useRouter } from "next/navigation";
import { Button } from "@/components/fun-ui/Button"
import { CreateOrEditSubjectDialog, ERROR_MESSAGE_NO_PAPERS_SELECTED } from "@/components/subjects/CreateOrEditSubjectDialog";
import { MicroscopePlusIcon, PlusCircleIcon, SearchIcon, FolderEmptyIcon } from "@/components/fun-ui/icons";
import { cn } from "@/lib/utils";
import { Tip } from "@/components/fun-ui/Tip";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/fun-ui/Popover";
import { Input } from "@/components/ui/input";
import { useSubjects, useFetchPapers } from "@/stores/appStore";
import { useSelection } from "@/hooks/useSelection";
import { useSubjectId } from "@/components/subjects/hooks/usePathParams";
import Loading from "@/components/Loading";

interface SubjectCreatorProps extends React.HTMLAttributes<HTMLFormElement> {
  ids: string[];
  iconOnly?: boolean;
  buttonLabel?: string;
  excludeSubjectId?: string;
}

export default function SubjectCreator({
  ids,
  iconOnly,
  buttonLabel = '主题分类',
  excludeSubjectId,
}: SubjectCreatorProps) {
  const { subjects, importPaper, fetchSubjects, subjectsFetched, fetchSubjectsLoading } = useSubjects();
  const router = useRouter();
  const subjectId = useSubjectId();
  const { resetRowSelection } = useSelection(subjectId);
  const { fetchPapers, setFetchPapersLoading } = useFetchPapers();
  const [searchQuery, setSearchQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  // 当浮层打开时，如果主题列表未加载，则主动获取
  useEffect(() => {
    if (open && !subjectsFetched && subjects.length === 0) {
      fetchSubjects(); // 显示 loading
    }
  }, [open, subjectsFetched, subjects.length, fetchSubjects]);

  // 过滤主题列表
  const filteredSubjects = useMemo(() => {
    const selectableSubjects = excludeSubjectId
      ? subjects.filter(subject => subject.id !== excludeSubjectId)
      : subjects;

    if (!searchQuery.trim()) {
      return selectableSubjects;
    }
    return selectableSubjects.filter(subject =>
      subject.subjectName.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [excludeSubjectId, subjects, searchQuery]);

  const hasOnlyExcludedSubject = !!excludeSubjectId && subjects.length > 0 && filteredSubjects.length === 0 && !searchQuery.trim();

  const handleImportToSubject = async (targetSubjectId: string) => {
    if (ids?.length <= 0) {
      toast.error(ERROR_MESSAGE_NO_PAPERS_SELECTED);
      return;
    }
    try {
      // 关闭浮层
      setOpen(false);
      // 设置 loading
      setFetchPapersLoading(true);
      // 静默模式导入，统一显示一个 toast
      await importPaper(targetSubjectId, ids.map(id => id.toString()), true);
      // 静默刷新当前 paperList
      await fetchPapers({ subjectId }, true);
      // 重置选中状态
      resetRowSelection();
      // 显示成功 toast（与 CreateOrEditSubjectDialog 中的样式一致）
      toast.success((t) => (
        <div className="flex items-center gap-3">
          <span>文献已导入主题</span>
          <button
            onClick={() => {
              router.push(`/subjects/${targetSubjectId}`);
              toast.dismiss(t.id);
            }}
            className="text-primary hover:underline"
          >
            查看
          </button>
        </div>
      ), {
        duration: 4000,
      });
    } catch (error) {
      console.error('导入文献失败:', error);
    } finally {
      // 确保 loading 状态总是被重置，避免 UI 冻结
      setFetchPapersLoading(false);
    }
  };


  return (
    <>
      {/* 受控的 Dialog，独立于 HoverCard */}
      <CreateOrEditSubjectDialog
        importPaperIds={ids}
        requireImportPapers={true}
        open={dialogOpen}
        onOpenChange={(open: boolean) => {
          setDialogOpen(open);
        }}
        onSuccess={() => {
          // 重置选中状态
          resetRowSelection();
          // 刷新主题列表
          fetchSubjects();
          // 静默刷新当前 paperList
          fetchPapers({ subjectId });
          // 关闭 Dialog
          setDialogOpen(false);
        }}
      />

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div>
            <Tip label={iconOnly ? buttonLabel : ''}>
              <Button
                variant="outline"
                className={cn(
                  "flex gap-1",
                  iconOnly ? "w-10 px-0" : "min-w-[130px]"
                )}
              >
                <MicroscopePlusIcon />
                <span className={cn(
                  iconOnly && "hidden"
                )}>{buttonLabel}</span>
              </Button>
            </Tip>
          </div>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-[280px] p-0"
        >
          <div className="flex flex-col">
            {/* 搜索框 */}
            <div className="px-5 pt-2 pb-2 border-b">
              <div className="relative flex items-center">
                <SearchIcon size={16} className="absolute left-0 text-annotation-foreground pointer-events-none" />
                <Input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="搜索主题"
                  className="pl-6 pr-3 h-9 text-sm text-annotation-foreground border-0 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent"
                />
              </div>
            </div>

            {/* 文献数量提示 */}
            <div className="px-5 pt-3 pb-2 text-xs text-annotation-foreground">
              {ids.length > 0 ? (
                `${ids.length} 篇文献导入到`
              ) : (
                '勾选文献，再选择主题即可导入'
              )}
            </div>

            {/* 主题列表 */}
            <div className="max-h-[266px] overflow-y-auto pb-2">
              {fetchSubjectsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loading size="sm" />
                </div>
              ) : filteredSubjects.length > 0 ? (
                <div>
                  {filteredSubjects.map((subject) => (
                    <div
                      key={subject.id}
                      onClick={() => handleImportToSubject(subject.id)}
                      className="px-3 py-3 mx-2 my-1 rounded-[4px] hover:bg-accent cursor-pointer transition-colors"
                      title={subject.subjectName}
                    >
                      <span className="text-sm block line-clamp-1 break-words h-5">
                        {subject.subjectName}
                      </span>
                    </div>
                  ))}
                </div>
              ) : subjects.length === 0 && !searchQuery ? (
                <div className="flex flex-col items-center justify-center h-full px-5 py-8">
                  <FolderEmptyIcon size={36} className="text-annotation-foreground mb-2.5" />
                  <div className="text-base font-medium text-foreground mb-2">
                    无主题分类
                  </div>
                  <div className="text-sm text-muted-foreground mb-6 text-center">
                    创建主题，方便文献管理与研究
                  </div>
                  <button
                    onClick={() => {
                      // 判断是否有选中文献
                      if (!ids || ids.length === 0) {
                        toast.error(ERROR_MESSAGE_NO_PAPERS_SELECTED);
                        return;
                      }
                      // 先打开 Dialog
                      setDialogOpen(true);
                      // 然后关闭浮层
                      setOpen(false);
                    }}
                    className="px-4 py-2 text-sm font-medium text-primary border border-primary rounded hover:bg-primary/10 transition-colors"
                  >
                    立即创建
                  </button>
                </div>
              ) : hasOnlyExcludedSubject ? (
                <div className="px-5 py-8 text-sm text-muted-foreground text-center">
                  暂无可同步的其他主题
                </div>
              ) : (
                <div className="px-5 py-8 text-sm text-muted-foreground text-center">
                  未找到匹配的主题
                </div>
              )}
            </div>

            {/* 分隔线 */}
            <div className="border-t"></div>

            {/* 新建主题按钮 */}
            <div className="px-5 py-2">
              <button
                onClick={() => {
                  // 判断是否有选中文献
                  if (!ids || ids.length === 0) {
                    toast.error(ERROR_MESSAGE_NO_PAPERS_SELECTED);
                    return;
                  }
                  // 先打开 Dialog
                  setDialogOpen(true);
                  // 然后关闭浮层
                  setOpen(false);
                }}
                className="w-full flex items-center gap-2 text-sm hover:text-primary rounded transition-colors py-2 cursor-pointer"
              >
                <PlusCircleIcon size={16} />
                <span>新主题分类</span>
              </button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </>
  )
}
